import React from 'react';
import { parseISO, format } from 'date-fns';

function formatDate(val) {
  if (!val) return '';
  try {
    return format(parseISO(val), 'dd-MMM-yyyy');
  } catch {
    return String(val).split('T')[0];
  }
}

const formatCellValue = (value, key) => {
  if (value === null || value === undefined || value === "") return "";
  
  // Handle already formatted values (from backend)
  if (typeof value === 'string' && value.includes(',')) {
    return value; // Already formatted with thousand separators
  }
  
  // Text fields: never run through numeric formatting
  if (key === 'transaction_type' || key === 'status' || key === 'deal_number' || key === 'isin' || key === 'portfolio' || key === 'counterparty' || key === 'series' || key === 'isin_issuer' || key === 'excel_source' || key === 'instrument_type' || key === 'quote_source' || key === 'deal_type') {
    return String(value);
  }

  const numValue = Number(value);
  if (isNaN(numValue)) return value;
  
  // Accrual/Amortization fields: show 8 decimals with thousand separators
  if (
    (key === 'daily_accrual' ||
      key === 'daily_amortization' ||
      key === 'cumulative_accrual' ||
      key === 'cumulative_amortization') &&
    numValue !== 0
  ) {
    return numValue.toLocaleString("en-US", {
      minimumFractionDigits: 8,
      maximumFractionDigits: 8
    });
  }

  // Coupon rate as percentage
  if (key === 'coupon_rate' && numValue !== 0) {
    return `${numValue.toFixed(2)}%`;
  }

  // Face value and currency amounts: show 2 decimals with thousand separators
  if ((key === 'face_value' || key === 'sell_back' || key === 'settlement_amount' || 
       key === 'maturity_amount' || key === 'amount' || key === 'clean_price_amount' || 
       key === 'dirty_price_amount' || key === 'leg1_clean_price_amount' || 
       key === 'leg2_clean_price_amount' || key === 'remaining_face_value' ||
       key === 'price_per_100' || key === 'per_day_accrual' || key === 'accrued_interest_to_date' ||
       key === 'discount_rate_pct' || key === 'brokerage' ||
       key === 'opening_balance' || key === 'sell_buy' || key === 'buy_sell' ||
       key === 'from' || key === 'to' || key === 'closing_balance' || key === 'in_hand' ||
       // Portfolio outstanding / summary views
       key === 'balance_amt' || key === 'sub_total' || key === 'repo_quar' ||
       key.startsWith('custodian_')) && numValue !== 0) {
    return numValue.toLocaleString("en-US", { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    });
  }
  
  // Price fields: show 4 decimals with thousand separators
  if ((key === 'buying_price' || key === 'selling_price' || key === 'average_price' || 
       key === 'dirty_price' || key === 'unrealized_gain' || key === 'clean_price' || 
       key === 'nvp' || key === 'repo_collateral' || key === 'available_balance' || 
       key === 'balance' || key === 'wap' || key === 'accrued_interest' || 
       key === 'coupon_interest' || key === 'leg1_clean_price' || key === 'leg1_dirty_price' || 
       key === 'leg2_clean_price' || key === 'leg2_dirty_price') && numValue !== 0) {
    return numValue.toLocaleString("en-US", { 
      minimumFractionDigits: 4, 
      maximumFractionDigits: 4 
    });
  }
  
  // Yield fields: show 4 decimals with thousand separators
  if ((key === 'buying_yield' || key === 'selling_yield' || key === 'average_yield' || 
       key === 'yield' || key === 'coupon_rate') && numValue !== 0) {
    return numValue.toLocaleString("en-US", { 
      minimumFractionDigits: 4, 
      maximumFractionDigits: 4 
    });
  }
  
  // DTM (Days to Maturity): show as integer with thousand separators
  if (key === 'dtm' && numValue !== 0) {
    return numValue.toLocaleString("en-US");
  }
  
  // For any other numeric values, use default formatting
  if (numValue !== 0) {
    return numValue.toLocaleString("en-US");
  }
  
  return value; // Leave non-numeric values unchanged
};

// Default columns for GSec report
const defaultColumns = [
  { key: 'portfolio', label: 'Portfolio' },
  { key: 'custodian', label: 'Custodian' },
  { key: 'deal_number', label: 'Deal Number' },
  { key: 'face_value', label: 'Face Value' },
  { key: 'coupon_rate', label: 'Coupon Rate' },
  { key: 'issue_date', label: 'Issue Date' },
  { key: 'value_date', label: 'Value Date' },
  { key: 'last_coupon_date', label: 'Last Coupon Date' },
  { key: 'next_coupon_date', label: 'Next Coupon Date' },
  { key: 'maturity_date', label: 'Maturity Date' },
  { key: 'isin', label: 'ISIN' },
  { key: 'coupon_interest', label: 'Coupon Interest' },
  { key: 'clean_price', label: 'Clean Price' },
  { key: 'dirty_price', label: 'Dirty Price' },
  { key: 'accrued_interest', label: 'Accrued Interest' },
  { key: 'clean_price_amount', label: 'Clean Price Amount' },
  { key: 'dirty_price_amount', label: 'Dirty Price Amount' },
  { key: 'nvp', label: 'NVP' },
  { key: 'yield', label: 'Yield' },
  { key: 'dtm', label: 'DTM' },
  { key: 'balance', label: 'Balance' },
  { key: 'available_balance', label: 'Available Balance' },
  { key: 'daily_accrual', label: 'Daily Accrual' },
  { key: 'daily_amortization', label: 'Daily Amortization' },
  { key: 'cumulative_accrual', label: 'Cumulative Accrual' },
  { key: 'cumulative_amortization', label: 'Cumulative Amortization' },
  { key: 'wap', label: 'WAP' },
  { key: 'repo_collateral', label: 'Repo Collateral' },
  { key: 'sell_back', label: 'Sell Back' },
  { key: 'counterparty', label: 'Counterparty' },
];

const gsecTransactionsColumns = [
  { key: 'deal_number', label: 'Deal Number' },
  { key: 'transaction_type', label: 'Transaction Type' },
  { key: 'portfolio', label: 'Portfolio' },
  { key: 'counterparty', label: 'Counterparty' },
  { key: 'isin', label: 'ISIN' },
  { key: 'trade_date', label: 'Trade Date' },
  { key: 'value_date', label: 'Value Date' },
  { key: 'maturity_date', label: 'Maturity Date' },
  { key: 'face_value', label: 'Face Value' },
  { key: 'clean_price', label: 'Clean Price' },
  { key: 'dirty_price', label: 'Dirty Price' },
  { key: 'yield', label: 'Yield' },
  { key: 'settlement_amount', label: 'Settlement Amount' },
  { key: 'coupon_rate', label: 'Coupon Rate' },
  { key: 'status', label: 'Status' },
];

// Money Market report columns (with P&L instead of WAP)
const moneyMarketColumns = [
  { key: 'portfolio', label: 'Portfolio' },
  { key: 'custodian', label: 'Custodian' },
  { key: 'deal_number', label: 'Deal Number' },
  { key: 'face_value', label: 'Face Value' },
  { key: 'value_date', label: 'Value Date' },
  { key: 'maturity_date', label: 'Maturity Date' },
  { key: 'isin', label: 'ISIN' },
  { key: 'coupon_interest', label: 'Coupon Interest' },
  { key: 'clean_price', label: 'Clean Price' },
  { key: 'yield', label: 'Yield' },
  { key: 'balance', label: 'Balance' },
  { key: 'pnl', label: 'P&L' },
  { key: 'counterparty', label: 'Counterparty' },
];

// Mark to Market report columns
const markToMarketColumns = [
  { key: 'series', label: 'Series' },
  { key: 'isin', label: 'ISIN' },
  { key: 'instrument_type', label: 'Instrument' },
  { key: 'quote_source', label: 'Quote Source' },
  { key: 'isin_issuer', label: 'ISIN Issuer' },
  { key: 'maturity_date', label: 'Maturity Date' },
  { key: 'buying_price', label: 'Buying Price' },
  { key: 'selling_price', label: 'Selling Price' },
  { key: 'average_price', label: 'Average Price' },
  { key: 'dirty_price', label: 'Dirty Price' },
  { key: 'buying_yield', label: 'Buying Yield (%)' },
  { key: 'selling_yield', label: 'Selling Yield (%)' },
  { key: 'average_yield', label: 'Average Yield (%)' },
  { key: 'balance', label: 'Balance' },
  { key: 'wap', label: 'WAP' },
  { key: 'unrealized_gain', label: 'Unrealized Gain' },
  { key: 'last_updated', label: 'Last Updated' },
  { key: 'excel_source', label: 'Source' },
];

// Portfolio Mark to Market columns (Finance's layout)
const markToMarketPortfolioColumns = [
  { key: 'maturity_date', label: 'Maturity' },
  { key: 'value_date', label: 'Value Date' },
  { key: 'face_value', label: 'Face Value Rs.' },
  { key: 'coupon', label: 'Coupon' },
  { key: 'purchased_yield', label: 'Purchased Yield' },
  { key: 'market_yield', label: 'Market Yield' },
  { key: 'quote_date', label: 'Quote Date' },
  { key: 'value_at_purchased_yield', label: 'Value as at Purchased Yield' },
  { key: 'value_at_market_yield', label: 'Value as at Market Yield' },
  { key: 'profit_loss', label: 'Profit (Loss) Rs.' },
];

// Repo / Reverse Repo report columns
const repoColumns = [
  { key: 'deal_number', label: 'Deal Number' },
  { key: 'deal_type', label: 'Deal Type' },
  { key: 'counterparty', label: 'Counterparty' },
  { key: 'settlement_mode', label: 'Settlement Mode' },
  { key: 'trade_date', label: 'Trade Date' },
  { key: 'value_date', label: 'Value Date' },
  { key: 'maturity_date', label: 'Maturity Date' },
  { key: 'principal_amount', label: 'Principal Amount' },
  { key: 'rate', label: 'Rate (%)' },
  { key: 'tenor', label: 'Tenor (Days)' },
  { key: 'interest_amount', label: 'Interest Amount' },
  { key: 'maturity_amount', label: 'Maturity Amount' },
  { key: 'isin', label: 'ISIN number' },
  { key: 'face_value_as_per_counterparty', label: 'Face value as per counterparty' }
];

// Portfolio report columns
const portfolioColumns = [
  { key: 'product_type', label: 'Product Type' },
  { key: 'deal_number', label: 'Deal Number' },
  { key: 'value_date', label: 'Value Date' },
  { key: 'trade_date', label: 'Trade Date' },
  { key: 'isin', label: 'ISIN' },
  { key: 'portfolio', label: 'Portfolio' },
  { key: 'counterparty', label: 'Counterparty' },
  { key: 'transaction_type', label: 'Transaction Type' },
  { key: 'face_value', label: 'Face Value' },
  { key: 'clean_price', label: 'Clean Price' },
  { key: 'dirty_price', label: 'Dirty Price' },
  { key: 'settlement_amount', label: 'Settlement Amount' },
  { key: 'maturity_amount', label: 'Maturity Amount' },
  { key: 'amount', label: 'Amount' },
  { key: 'maturity_date', label: 'Maturity Date' },
  { key: 'status', label: 'Status' },
  { key: 'currency', label: 'Currency' }
];

// Counterparty report columns
const counterpartyColumns = [
  { key: 'short_name', label: 'Counterparty' },
  { key: 'deal_number', label: 'Deal Number' },
  { key: 'deal_source', label: 'Deal Source' },
  { key: 'product_type', label: 'Product Type' },
  { key: 'trade_date', label: 'Trade Date' },
  { key: 'value_date', label: 'Value Date' },
  { key: 'amount', label: 'Amount' },
  { key: 'price', label: 'Price' },
  { key: 'yield', label: 'Yield' },
  { key: 'portfolio', label: 'Portfolio' },
  { key: 'isin', label: 'ISIN' },
  { key: 'maturity_date', label: 'Maturity Date' },
  { key: 'status', label: 'Status' },
  { key: 'currency', label: 'Currency' }
];

// Buyback AST report columns (one row per deal)
const buybackColumns = [
  { key: 'portfolio', label: 'Portfolio' },
  { key: 'counterparty', label: 'Counterparty' },
  { key: 'deal_number', label: 'Deal Number' },
  { key: 'transaction_type', label: 'Transaction Type' },
  { key: 'isin', label: 'ISIN' },
  { key: 'face_value', label: 'Face Value' },
  { key: 'leg1_clean_price', label: 'Leg1 Clean Price' },
  { key: 'leg1_dirty_price', label: 'Leg1 Dirty Price' },
  { key: 'leg1_clean_price_amount', label: 'Leg1 Clean Price Amount' },
  { key: 'value_date', label: 'Value Date (1st Leg)' },
  { key: 'maturity_date', label: 'Maturity Date (2nd Leg)' },
  { key: 'settlement_value', label: 'Settlement Value' },
  { key: 'maturity_value', label: 'Maturity Value' },
  { key: 'leg2_clean_price', label: 'Leg2 Clean Price' },
  { key: 'leg2_dirty_price', label: 'Leg2 Dirty Price' },
  { key: 'leg2_clean_price_amount', label: 'Leg2 Clean Price Amount' },
  { key: 'rate', label: 'Rate' },
  { key: 'dtm', label: 'DTM' },
];

// T-Bill report columns
const tbillColumns = [
  { key: 'deal_number', label: 'Deal Number' },
  { key: 'trade_date', label: 'Trade Date' },
  { key: 'value_date', label: 'Value Date' },
  { key: 'transaction_type', label: 'Transaction' },
  { key: 'counterparty', label: 'Counterparty' },
  { key: 'isin_number', label: 'ISIN' },
  { key: 'maturity_date', label: 'Maturity Date' },
  { key: 'face_value', label: 'Face Value' },
  { key: 'discount_rate_pct', label: 'Discount Rate (%)' },
  { key: 'days_to_maturity', label: 'Days to Maturity' },
  { key: 'price_per_100', label: 'Price per 100' },
  { key: 'settlement_amount', label: 'Settlement Amount' },
  { key: 'portfolio', label: 'Portfolio' },
  { key: 'remaining_face_value', label: 'Remaining Face Value' },
  { key: 'per_day_accrual', label: 'Per Day Accrual' },
  { key: 'accrued_interest_to_date', label: 'Accrued Interest to Date' },
];

const tbillTransactionsColumns = [
  { key: 'deal_number', label: 'Deal Number' },
  { key: 'transaction_type', label: 'Transaction' },
  { key: 'portfolio', label: 'Portfolio' },
  { key: 'counterparty', label: 'Counterparty' },
  { key: 'isin_number', label: 'ISIN' },
  { key: 'trade_date', label: 'Trade Date' },
  { key: 'value_date', label: 'Value Date' },
  { key: 'maturity_date', label: 'Maturity Date' },
  { key: 'face_value', label: 'Face Value' },
  { key: 'discount_rate_pct', label: 'Discount Rate (%)' },
  { key: 'price_per_100', label: 'Price per 100' },
  { key: 'settlement_amount', label: 'Settlement Amount' },
  { key: 'status', label: 'Status' },
];

// Broker report columns
const brokerColumns = [
  { key: 'broker_name', label: 'Broker Name' },
  { key: 'ref_deal_no', label: 'Ref Deal No' },
  { key: 'counterparty', label: 'Counterparty' },
  { key: 'face_value', label: 'FV' },
  { key: 'brokerage', label: 'Brokerage' },
];

const ReportTable = ({
  data,
  pagination,
  loading,
  error,
  onPageChange,
  reportType = 'gsec',
  onFaceValueClick,
  columns: columnsOverride,
  footerRow,
  emphasisKeys = [],
  clickableKeys = [],
  onCellClick
}) => {
  // Choose columns based on report type
  let columns = defaultColumns;
  if (!columnsOverride) {
    if (reportType === 'money-market') {
      columns = moneyMarketColumns;
    } else if (reportType === 'mark-to-market-portfolio') {
      columns = markToMarketPortfolioColumns;
    } else if (reportType === 'mark-to-market') {
      columns = markToMarketColumns;
    } else if (reportType === 'repo') {
      columns = repoColumns;
    } else if (reportType === 'portfolio') {
      columns = portfolioColumns;
    } else if (reportType === 'counterparty') {
      columns = counterpartyColumns;
    } else if (reportType === 'buyback') {
      columns = buybackColumns;
    } else if (reportType === 'tbill') {
      columns = tbillColumns;
    } else if (reportType === 'tbill-transactions') {
      columns = tbillTransactionsColumns;
    } else if (reportType === 'gsec-transactions') {
      columns = gsecTransactionsColumns;
    } else if (reportType === 'broker') {
      columns = brokerColumns;
    }
  } else {
    columns = columnsOverride;
  }
  const { page = 1, pageSize = 20, total = 0 } = pagination || {};
  const canPage = typeof onPageChange === 'function';
  const totalPages = Math.ceil(total / pageSize);

  const renderCellContent = (row, col) => {
    if (col.key === 'value_date' || col.key === 'maturity_date' || col.key === 'trade_date' || col.key === 'issue_date' || col.key === 'last_coupon_date' || col.key === 'next_coupon_date' || col.key === 'last_updated' || col.key === 'quote_date') {
      return row[col.key] ? formatDate(row[col.key]) : '';
    }
    if (col.key === 'face_value' && (reportType === 'gsec' || reportType === 'gsec-transactions') && typeof onFaceValueClick === 'function') {
      return (
        <button
          type="button"
          onClick={() => onFaceValueClick(row)}
          className="text-blue-600 hover:underline font-medium bg-transparent border-none cursor-pointer p-0"
          title="View sell history for this deal"
        >
          {formatCellValue(row[col.key], col.key)}
        </button>
      );
    }
    const isClickable =
      clickableKeys.includes(col.key) &&
      typeof onCellClick === 'function' &&
      Number(row[col.key]) !== 0;
    if (isClickable) {
      return (
        <button
          type="button"
          onClick={() => onCellClick(row, col.key)}
          className="text-blue-600 hover:underline font-medium bg-transparent border-none cursor-pointer p-0"
          title="View deal breakdown"
        >
          {formatCellValue(row[col.key], col.key)}
        </button>
      );
    }
    return formatCellValue(row[col.key], col.key);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-soft p-3">
      {loading && <div className="my-3 text-gray-600">Loading...</div>}
      {error && <div className="my-3 p-3 bg-red-50 text-red-700 rounded-md border border-red-200">{error}</div>}
      {!loading && !error && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr>
                  {columns.map(col => (
                    <th key={col.key} className="text-left px-4 py-3 bg-gray-50 font-semibold text-gray-700 border-b-2 border-gray-200">
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data && data.length > 0 ? (
                  data.map((row, idx) => (
                    <tr
                      key={idx}
                      className={row._rowClass ? `${row._rowClass} hover:bg-gray-50` : 'hover:bg-gray-50'}
                    >
                      {columns.map(col => {
                        const cellContent = renderCellContent(row, col);
                        const isEmphasis = emphasisKeys.includes(col.key);
                        return (
                          <td
                            key={col.key}
                            title={row._cellTitles?.[col.key] || undefined}
                            className={`px-4 py-3 text-center border-b border-gray-100${isEmphasis ? ' font-semibold' : ''}${row._cellTitles?.[col.key] ? ' text-amber-700' : ''}`}
                          >
                            {cellContent}
                            {row._cellTitles?.[col.key] ? ' ⚠' : ''}
                          </td>
                        );
                      })}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={columns.length} className="text-center py-5 text-gray-500">
                      No data found.
                    </td>
                  </tr>
                )}
              </tbody>
              {footerRow && (
                <tfoot>
                  <tr className="font-bold border-t-2 border-gray-800 bg-gray-50">
                    {columns.map((col) => (
                      <td key={col.key} className="px-4 py-3 text-center">
                        {col.key === 'maturity_date' || col.key === 'isin' || col.key === 'coupon_rate'
                          ? (footerRow[col.key] ?? '')
                          : formatCellValue(footerRow[col.key], col.key)}
                      </td>
                    ))}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="mt-4 flex justify-center items-center gap-3">
              <button
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md border border-gray-300 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!canPage || page <= 1}
                onClick={() => {
                  if (canPage) onPageChange({ ...pagination, page: page - 1 });
                }}
              >
                Prev
              </button>
              <span className="text-sm text-gray-600">Page {page} of {totalPages}</span>
              <button
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md border border-gray-300 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!canPage || page >= totalPages}
                onClick={() => {
                  if (canPage) onPageChange({ ...pagination, page: page + 1 });
                }}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ReportTable;
